/* eslint-disable max-len */
import React, {
  useContext, useEffect, useState,
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import window from 'global/window';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import produce from 'immer';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import Button from '@mui/material/Button';
// eslint-disable-next-line import/no-extraneous-dependencies
import TextLink from '@mui/material/Link';
import Typography from '@digital-hig/mui/dist/Typography';
// eslint-disable-next-line import/no-extraneous-dependencies
// import FormControlLabel from '@mui/material/FormControlLabel';
// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import Checkmark from '@digital-hig/icon/lib/esm/display-icons/complete';
import SvgCaretRight from '@digital-hig/icon/lib/esm/icons/caret-right';
import SvgError from '@digital-hig/icon/lib/esm/display-icons/error';
import { completeProblem, hasCompletedRecord } from '../common/userUtils';
import {
  QUIZ_TYPE_SINGLE,
  QUIZ_TYPE_MULTI,
  QUIZ_TYPE_ORDERING,
  QUIZ_TYPE_MATCHING,
  ENVIRONMENT,
  REQUEST_FLAGS,
  BANNER_TYPE_INFO,
  BANNER_TYPE_ERROR,
  // AA_EVENT_START,
  ROUTER_BASENAME,
  VERSION,
  ITEM_TYPE_QUIZ,
  LRS_VERB_COMPLETED,
} from '../common/constants';
import logger from '../common/logger';
import Banner from './bannerHOC';
import {
  shuffleArray,
  getCourseAPIHost,
  getEnvironmentConstant,
  generateLRSID,
} from '../common/utilities';
import EnvContext from '../common/envContext';
import {
  getLearningRecords,
  getUserDetails, isAuthChecked,
} from '../store/userSlice';

import { LoginClickHandler } from '../common/login';
import QuizTypeSingleAndMulti from './quizTypeSingleAndMulti';
import QuizTypeOrdering from './quizTypeOrdering';
import QuizTypeMatching from './quizTypeMatching';
import { triggerProblemResult } from '../common/analyticsApi';

const questionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  explanation: PropTypes.string,
  text: PropTypes.string.isRequired,
  answers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  minimumAnswers: PropTypes.number.isRequired,
});

const Quiz = ({
  data, unitId, curatedData, tutorialData, setHasQuizPassed,
}) => {
  const [answers, setAnswers] = useState({});
  const [csrRandomized, setCsrRandomized] = useState(false);
  const [quizState, setQuizState] = useState({
    questions: JSON.parse(JSON.stringify(data.questions)),
    canSubmit: false,
    showReset: false,
    showUnanswered: false,
    unansweredQuestions: data?.questions?.filter((q) => q.type !== QUIZ_TYPE_ORDERING).map((q) => q.id),
  });
  const dispatch = useDispatch();
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const appVersion = envContext[VERSION];

  const { isPreview } = envContext[REQUEST_FLAGS] || false;
  const userDetails = useSelector(getUserDetails);
  const authChecked = useSelector(isAuthChecked);
  const learningRecords = useSelector(getLearningRecords);

  const quizErrorMessage = 'An error occurred, please try again.';

  const scrollTo = (id) => {
    const obj = document.getElementById(id);
    if (obj) {
      const appNav = document.getElementById('appNav');
      const offset = appNav ? appNav.offsetHeight + appNav.offsetTop + 20 : 20; // sticky nav height + buffer
      window.scroll({ top: (obj.offsetTop - offset), left: 0, behavior: 'smooth' });
    }
  };

  const makingOrderingProbAsAnswered = (currentQuizState) => {
    // as considering ordering problem types have been answered by default
    const ansMap = {};
    currentQuizState.questions?.filter((q) => q.type === QUIZ_TYPE_ORDERING).forEach((o) => {
      o.answers.forEach((ans) => {
        if (Array.isArray(ansMap[o.id])) {
          ansMap[o.id].push(ans.id);
        } else {
          ansMap[o.id] = [ans.id];
        }
      });
    });
    if (!quizState.showReset) {
      setAnswers({ ...answers, ...ansMap });
    } else {
      setAnswers(ansMap);
    }
  };
  const randomizeQuiz = (state = quizState) => {
    const updatedQuizState = produce(state, (draftState) => {
      if (data.randomizeProblems) {
        shuffleArray(draftState.questions);
      }
      if (draftState.questions) {
        draftState.questions.forEach((q) => {
          if (q.randomize) {
            shuffleArray(q.answers);
            if (q.type === QUIZ_TYPE_MATCHING) {
              shuffleArray(q.terms);
            }
          }
        });
      }
      draftState.unansweredQuestions = draftState?.questions?.filter((q) => q.type !== QUIZ_TYPE_ORDERING).map((q) => q.id);
    });
    setQuizState(updatedQuizState);
    makingOrderingProbAsAnswered(updatedQuizState);
  };

  const resetQuiz = () => {
    const updatedQuizState = produce(quizState, (draftState) => {
      Object.keys(draftState).forEach((key) => delete draftState[key]);
      draftState.questions = JSON.parse(JSON.stringify(data.questions));
      draftState.canSubmit = false;
      draftState.showReset = false;
      draftState.passed = false;
      draftState.unansweredQuestions = draftState?.questions?.filter((q) => q.type !== QUIZ_TYPE_ORDERING).map((q) => q.id);
    });
    setAnswers({});
    randomizeQuiz(updatedQuizState);
  };

  useEffect(() => {
    if (data.graded && !csrRandomized) { // Is long form test - randomize in client
      setCsrRandomized(true);
      randomizeQuiz();
    }
  }, []);

  const areAllQuestionsAnswered = (updatedAnswers = answers) => {
    const answerKeys = Object.keys(updatedAnswers).filter((a) => {
      const answeredQuestion = data.questions.filter((q) => q.id === a)[0];

      // Do not return an answer key for questions who's minimum answer count has not been met
      if (answeredQuestion.type === QUIZ_TYPE_MULTI && answeredQuestion.minimumAnswers && updatedAnswers[a].length < answeredQuestion.minimumAnswers) {
        return false;
      }

      return true;
    });
    const questionKeys = data.questions.filter((q) => (q.type === QUIZ_TYPE_SINGLE || q.type === QUIZ_TYPE_MULTI || q.type === QUIZ_TYPE_ORDERING || q.type === QUIZ_TYPE_MATCHING)).map((q) => q.id);
    return answerKeys.sort().join(',') === questionKeys.sort().join(',');
  };

  const jumpToFirstUnanswered = () => {
    const unansweredQuestions = quizState.questions.filter((q) => {
      if (quizState.unansweredQuestions.includes(q.id)) {
        return true;
      }
      if (answers[q.id] && q.type === QUIZ_TYPE_MULTI && q.minimumAnswers && answers[q.id].length < q.minimumAnswers) {
        return true;
      }

      return false;
    });
    if (unansweredQuestions.length) {
      scrollTo(`qid${unansweredQuestions[0].id}`);
    }
  };

  const validate = (event) => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.target.disabled = true;

    if (areAllQuestionsAnswered()) {
      const host = getCourseAPIHost({ env, isPreview });
      axios.post(`${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/quiz/v1/${data.id}`, { answers }).then((resp) => {
        const { data: respData } = resp;
        const updatedQuizState = produce(quizState, (draftState) => {
          // Clear any previous error state
          delete draftState.error;

          draftState.showReset = true;
          draftState.canSubmit = false;
          draftState.passed = respData?.resp?.passed || false;
          draftState.correct = respData?.resp?.correct;
          draftState.questions.forEach((question, idx) => {
            const { id } = question;
            if (respData?.resp?.correct && respData?.resp?.correct.includes(id)) {
              draftState.questions[idx].isCorrect = true;
              delete draftState.questions[idx].explanation;
            }

            if (respData?.resp?.incorrect && respData?.resp?.incorrect[id]) {
              draftState.questions[idx].isCorrect = false;
              draftState.questions[idx].explanation = respData?.resp?.incorrect[id].explanation;
            }
          });
        });
        setQuizState(updatedQuizState);

        // Handle LRS Data Push
        if (updatedQuizState.passed) {
          completeProblem({
            dispatch, unitId, curatedData, userDetails, env, problemId: data.id, appVersion, tutorialData,
          });
        }

        // Handle Analytics
        triggerProblemResult({
          courseData: curatedData,
          moduleData: tutorialData,
          unitId,
          problemInfo: {
            blockId: data.id,
            // attemptsUsed: problemResult.attempts_used,
            currentScore: respData?.resp?.correct?.length || 0,
            totalPossibleScore: updatedQuizState.questions.length,
            result: updatedQuizState.passed ? 'pass' : 'fail',
          },
          isUnitComplete: hasCompletedRecord({ learningRecords, unitId, env }),
        });

        const unit = curatedData ? curatedData.unitLookup[unitId] : tutorialData.unitLookup[unitId];
        const unfinishedSiblings = unit.content.filter((item) => {
          // Shortcut for current item
          if (item.id === data.id) {
            return false;
          }
          const isProblem = item.type === ITEM_TYPE_QUIZ;
          let hasRecord = false;
          // Fetch records for problem
          if (isProblem) {
            const problemRecords = learningRecords[generateLRSID({ id: item.id, env })];
            hasRecord = problemRecords && problemRecords.statements
                && problemRecords.statements.filter((statement) => statement.verb === LRS_VERB_COMPLETED).length !== 0;
          }
          // Return true for any sibling problem that isn't completed
          return isProblem && !hasRecord;
        });
        if (updatedQuizState.passed && !unfinishedSiblings.length) {
          setHasQuizPassed(true);
        }
      }).catch((e) => {
        logger.error('Failed parsing course validation: ', e);
        setQuizState(produce(quizState, (draftState) => {
          // Clear any previous passed state
          delete draftState.passed;
          draftState.error = { msg: quizErrorMessage, errorMessage: e.message, errorCode: e?.response?.status || 500 };
        }));
      });
    } else {
      setQuizState(produce(quizState, (draftState) => {
        // Clear any previous passed state
        delete draftState.passed;
        draftState.showUnanswered = true;
      }));

      jumpToFirstUnanswered();
    }
  };

  const isEveryQuestionAnswered = (updatedAnswers, updatedQuizState) => {
    if (areAllQuestionsAnswered(updatedAnswers)) {
      const newState = produce(updatedQuizState, (draftState) => {
        draftState.canSubmit = true;
        draftState.unansweredQuestions = [];
      });
      setQuizState(newState);
    } else {
      const unansweredQuesitonIds = updatedQuizState.questions.map((q) => q.id);
      const unansweredQuestions = unansweredQuesitonIds.filter((qid) => updatedAnswers[qid] === undefined || updatedAnswers[qid].length < (quizState.questions.find((q) => q.id === qid).minimumAnswers || 0));
      const newState = produce(updatedQuizState, (draftState) => {
        draftState.canSubmit = false;
        draftState.unansweredQuestions = unansweredQuestions;
      });
      setQuizState(newState);
    }
  };

  if (!data.graded || csrRandomized) {
    return (
      <RootElement>
        {authChecked && !userDetails.userid ? (
          <Banner type={BANNER_TYPE_INFO}>
            <TextLink variant="text" onClick={LoginClickHandler} data-wat-linkname="sign in quiz banner">Sign in</TextLink>
            {' '}
            to take the quiz.
          </Banner>
        ) : <></>}

        <Typography component="h2" variant="headline-medium">
          {data.text}
        </Typography>
        {data.graded && (
          <Typography variant="body-copy-medium">Required for course completion</Typography>
        )}
        {data?.questions?.length && data.questions.length >= 3 && (
          <Typography variant="body-copy-medium">
            {data.questions.length}
            {' '}
            questions
          </Typography>
        )}
        <form id={data.id} onSubmit={validate}>
          {quizState?.questions?.map((question) => {
            switch (question.type) {
            case QUIZ_TYPE_SINGLE:
            case QUIZ_TYPE_MULTI:
              return (
                <QuizTypeSingleAndMulti
                  isSignedIn={authChecked && userDetails.userid}
                  question={question}
                  answers={answers}
                  setAnswers={setAnswers}
                  quizState={quizState}
                  isEveryQuestionAnswered={isEveryQuestionAnswered}
                />
              );
            case QUIZ_TYPE_ORDERING:
              return (
                <QuizTypeOrdering
                  isSignedIn={authChecked && userDetails.userid}
                  question={question}
                  setQuizState={setQuizState}
                  answers={answers}
                  setAnswers={setAnswers}
                  quizState={quizState}
                  makingOrderingProbAsAnswered={makingOrderingProbAsAnswered}
                />
              );
            case QUIZ_TYPE_MATCHING:
              return (
                <QuizTypeMatching
                  isSignedIn={authChecked && userDetails.userid}
                  question={question}
                  setQuizState={setQuizState}
                  answers={answers}
                  setAnswers={setAnswers}
                  quizState={quizState}
                  isEveryQuestionAnswered={isEveryQuestionAnswered}
                />
              );
            default:
              return (
                <div key={question.id}>
                  Invalid Question Data:
                  {question.type}
                </div>
              );
            }
          })}
          <Grid container flexDirection="column">

            {(userDetails.userid && !quizState.showReset) || !authChecked ? (
              <Grid item>
                <Button
                  type="submit"
                  value="submit"
                  size="large"
                  variant="contained"
                  disabled={!authChecked}
                  className="submit"
                  endIcon={(<SvgIcon size="small"><SvgCaretRight /></SvgIcon>)}
                >
                  Submit
                </Button>
              </Grid>
            ) : (
              <Grid item>
                {authChecked && !userDetails.userid && (
                  <Button
                    onClick={LoginClickHandler}
                    size="large"
                    variant="contained"
                    className="signIn"
                    endIcon={(<SvgIcon size="small"><SvgCaretRight /></SvgIcon>)}
                    data-wat-linkname="sign in quiz button"
                  >
                    Sign in to take the quiz
                  </Button>
                )}
              </Grid>
            )}

            {quizState.showReset && (
              <Grid item>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={resetQuiz}
                  className="resetQuiz"
                  endIcon={(<SvgIcon size="small"><SvgCaretRight /></SvgIcon>)}
                >
                  Reset
                </Button>
              </Grid>
            )}

            {quizState?.unansweredQuestions?.length !== 0 && (
              <Grid item>
                <Typography color={quizState.showUnanswered ? 'ink-error' : 'ink-on-background'}>
                  {quizState.showUnanswered && <SvgIcon size="medium"><SvgError /></SvgIcon>}
                  { `${quizState?.unansweredQuestions?.length}/${quizState?.questions?.length} questions left unanswered`}
                </Typography>
              </Grid>
            )}
            {quizState.showReset && (
              <Grid item>
                {quizState.passed && (<SvgIcon size="medium"><Checkmark /></SvgIcon>)}
                { ' '}
                <Typography component="span" variant="body-copy-small" color="ink-on-background-60">
                  {quizState?.correct?.length || 0}
                  /
                  {quizState?.questions?.length}
                  {' '}
                  correct answers
                </Typography>
              </Grid>
            )}
            {quizState.error && (
              <Grid item>
                <Banner type={BANNER_TYPE_ERROR}>{quizState.error.msg}</Banner>
              </Grid>
            )}
          </Grid>
        </form>
      </RootElement>

    );
  }
  return <></>;
};

const RootElement = styled.div`
 .MuiGrid-item{
  padding-left: 0;
 }
`;

Quiz.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    graded: PropTypes.bool.isRequired,
    randomizeProblems: PropTypes.bool,
    resp: PropTypes.shape({
      passed: PropTypes.bool,
      correct: PropTypes.arrayOf(PropTypes.string),
      incorrect: PropTypes.arrayOf(PropTypes.shape({ explanation: PropTypes.string })),
    }),
    questions: PropTypes.arrayOf(questionPropType).isRequired,
  }).isRequired,
  curatedData: PropTypes.shape({ unitLookup: PropTypes.shape({ content: PropTypes.arrayOf(PropTypes.shape({})) }) }),
  unitId: PropTypes.string.isRequired,
  tutorialData: PropTypes.shape({ unitLookup: PropTypes.shape({}) }),
  unitCompleteRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
  setHasQuizPassed: PropTypes.func.isRequired,
};

Quiz.defaultProps = {
  curatedData: {},
  tutorialData: {},
  // unitId: undefined,
};

export default Quiz;
